import type { IPeriodFacts } from '../types/metrics';
import type { TableRowKind } from '../types/tables';
import { BUCKET_LABELS, OPEX_BUCKETS } from './BucketHelper';

export interface IStatementRowSpec {
  key: string;
  label: string;
  pick: (facts: IPeriodFacts) => number;
  margin?: (facts: IPeriodFacts) => number | null;
  kind?: TableRowKind;
  indent?: number;
}

export interface IWorkingCapitalRowSpec {
  key: string;
  label: string;
  pick: (facts: IPeriodFacts) => number | null;
  basis: string;
}

/** Row definitions for the P&L, balance sheet and working-capital table components. */
export const PNL_ROWS: IStatementRowSpec[] = [
  {
    key: 'revenue',
    label: 'Revenue',
    pick: (f) => f.pnl.revenue,
    kind: 'subtotal',
  },
  {
    key: 'cogs',
    label: 'Cost of goods sold',
    pick: (f) => -f.pnl.costOfGoodsSold,
    indent: 1,
  },
  {
    key: 'gross_profit',
    label: 'Gross profit',
    pick: (f) => f.pnl.grossProfit,
    margin: (f) => f.pnl.grossMargin,
    kind: 'total',
  },
  ...OPEX_BUCKETS.map<IStatementRowSpec>((b) => ({
    key: b,
    label: BUCKET_LABELS[b],
    pick: (f) => -f.buckets[b],
    indent: 1,
  })),
  {
    key: 'opex',
    label: 'Total operating expenses',
    pick: (f) => -f.pnl.opex,
    kind: 'subtotal',
  },
  {
    key: 'ebitda',
    label: 'EBITDA',
    pick: (f) => f.pnl.ebitda,
    margin: (f) => f.pnl.ebitdaMargin,
    kind: 'total',
  },
  {
    key: 'operating_profit',
    label: 'Operating profit (EBIT)',
    pick: (f) => f.pnl.operatingProfit,
    kind: 'subtotal',
  },
  {
    key: 'non_op_income',
    label: 'Non-operating income',
    pick: (f) => f.pnl.nonOperatingIncome,
    indent: 1,
  },
  {
    key: 'non_op_expense',
    label: 'Non-operating expense',
    pick: (f) => -f.pnl.nonOperatingExpense,
    indent: 1,
  },
  {
    key: 'tax',
    label: 'Income tax',
    pick: (f) => -f.pnl.incomeTax,
    indent: 1,
  },
  {
    key: 'net_profit',
    label: 'Net profit',
    pick: (f) => f.pnl.netProfit,
    margin: (f) => f.pnl.netMargin,
    kind: 'total',
  },
];

export const BALANCE_SHEET_ROWS: IStatementRowSpec[] = [
  { key: 'h_assets', label: 'Assets', pick: () => 0, kind: 'header' },
  { key: 'cash', label: 'Cash', pick: (f) => f.balance.cash, indent: 1 },
  {
    key: 'investments',
    label: 'Investments',
    pick: (f) => f.balance.investments,
    indent: 1,
  },
  {
    key: 'ar',
    label: 'Accounts receivable',
    pick: (f) => f.balance.accountsReceivable,
    indent: 1,
  },
  {
    key: 'inventory',
    label: 'Inventory',
    pick: (f) => f.balance.inventory,
    indent: 1,
  },
  {
    key: 'oca',
    label: 'Other current assets',
    pick: (f) => f.balance.otherCurrentAssets,
    indent: 1,
  },
  {
    key: 'fixed',
    label: 'Fixed assets (net)',
    pick: (f) => f.balance.fixedAssets,
    indent: 1,
  },
  {
    key: 'onca',
    label: 'Other non-current assets',
    pick: (f) => f.balance.otherNonCurrentAssets,
    indent: 1,
  },
  {
    key: 'total_assets',
    label: 'Total assets',
    pick: (f) => f.balance.totalAssets,
    kind: 'total',
  },
  { key: 'h_liab', label: 'Liabilities', pick: () => 0, kind: 'header' },
  {
    key: 'ap',
    label: 'Accounts payable',
    pick: (f) => f.balance.accountsPayable,
    indent: 1,
  },
  {
    key: 'ocl',
    label: 'Other current liabilities',
    pick: (f) => f.balance.otherCurrentLiabilities,
    indent: 1,
  },
  { key: 'debt', label: 'Debt', pick: (f) => f.balance.debt, indent: 1 },
  {
    key: 'oncl',
    label: 'Other non-current liabilities',
    pick: (f) => f.balance.otherNonCurrentLiabilities,
    indent: 1,
  },
  {
    key: 'total_liab',
    label: 'Total liabilities',
    pick: (f) => f.balance.totalLiabilities,
    kind: 'total',
  },
  { key: 'h_eq', label: 'Equity', pick: () => 0, kind: 'header' },
  {
    key: 'share_capital',
    label: 'Share capital & premium',
    pick: (f) => f.balance.shareCapital,
    indent: 1,
  },
  {
    key: 'retained',
    label: 'Retained earnings',
    pick: (f) => f.balance.retainedEarnings,
    indent: 1,
  },
  {
    key: 'other_eq',
    label: 'Other equity',
    pick: (f) => f.balance.otherEquity,
    indent: 1,
  },
  {
    key: 'total_eq',
    label: 'Total equity',
    pick: (f) => f.balance.totalEquity,
    kind: 'total',
  },
  {
    key: 'check',
    label: 'Liabilities + equity',
    pick: (f) => f.balance.totalLiabilities + f.balance.totalEquity,
    kind: 'subtotal',
  },
];

export const WORKING_CAPITAL_ROWS: IWorkingCapitalRowSpec[] = [
  {
    key: 'dso',
    label: 'DSO',
    pick: (f) => f.workingCapital.dso,
    basis: 'AR ÷ revenue × days',
  },
  {
    key: 'dpo',
    label: 'DPO',
    pick: (f) => f.workingCapital.dpo,
    basis: 'AP ÷ COGS × days',
  },
  {
    key: 'dio',
    label: 'DIO',
    pick: (f) => f.workingCapital.dio,
    basis: 'Inventory ÷ COGS × days',
  },
  {
    key: 'ccc',
    label: 'Cash conversion cycle',
    pick: (f) => f.workingCapital.cashConversionCycle,
    basis: 'DSO + DIO − DPO',
  },
];
